import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

import { Button } from "@/design-system/Button";
import { checkFacts, originFacts, versionFacts } from "@/features/system/ProductionRelease";
import type { ReleaseStatusResponse } from "@/shared/api";
import { componentReleaseIdentity } from "@/shared/release/componentRelease";
import { releaseVerdict } from "@/shared/release/releaseLanguage";
import { useReleaseStatus } from "@/shared/release/useReleaseStatus";

const TONE: Record<ReleaseStatusResponse["status"], string> = {
  UP_TO_DATE: "text-status-positive",
  NEWER_MAIN_AVAILABLE: "text-status-warning",
  RELEASE_DRIFT: "text-status-negative",
  UNKNOWN: "text-text-muted",
};

/** The same facts the Система screen shows, in the same words, compactly. */
function Evidence({ release }: { readonly release: ReleaseStatusResponse }): React.JSX.Element {
  const facts = [...versionFacts(release), ...checkFacts(release), ...originFacts(release)];
  return (
    <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-helper-sm">
      {facts.map((fact) => (
        <div key={fact.label} className="contents">
          <dt className="text-text-muted">{fact.label}</dt>
          <dd
            className="min-w-0 break-all text-right text-text-primary"
            title={fact.exact}
            data-code={fact.code}
          >
            {fact.verdict?.text ?? fact.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Compact global answer to which independently delivered components are active. */
export function ReleaseIndicator(): React.JSX.Element {
  const state = useReleaseStatus();
  const [open, setOpen] = useState(false);
  const release = state.status === "ready" ? state.release : null;
  const component = componentReleaseIdentity();
  const releaseState: ReleaseStatusResponse["status"] = release?.status ?? "UNKNOWN";
  const verdict = release === null ? "проверяется" : releaseVerdict(release).text;
  const shortSha = component?.webSha.slice(0, 8);

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="cell"
        aria-expanded={open}
        aria-label={`Релиз в Production: ${verdict}`}
        data-status={releaseState}
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        <span className={`flex items-center gap-1.5 px-1.5 ${TONE[releaseState]}`}>
          <span>Версия</span>
          {shortSha !== undefined && <span aria-hidden className="font-mono">· {shortSha}</span>}
          <span aria-hidden>· {verdict}</span>
        </span>
        {open ? <ChevronDown aria-hidden className="size-3" /> : <ChevronUp aria-hidden className="size-3" />}
      </Button>
      {open && release !== null && (
        <section
          data-testid="release-indicator-details"
          aria-label="Релиз в Production: подробности"
          className="absolute right-0 bottom-7 z-50 w-[min(92vw,34rem)] rounded-panel border border-border-subtle bg-surface-default p-3 shadow-lg"
        >
          <div className="mb-2 flex items-center justify-between gap-3 border-b border-border-subtle pb-2">
            <p className="text-label-caps text-text-secondary">Релиз в Production</p>
            <p className={`text-helper-sm ${TONE[release.status]}`}>{verdict}</p>
          </div>
          <Evidence release={release} />
        </section>
      )}
    </div>
  );
}
